import {
  BrowserRouter as Router,
  Redirect,
  Route,
  Switch,
} from 'react-router-dom'
import Footer from './common/Footer'
import React, { useEffect, useRef, useState } from 'react'
import Routes, { IRoute, PATHS } from './Routes'
import NavMenu from './common/NavMenu'
import Breadcrumbs from './common/Breadcrumbs'
import { IBreadcrumbItem } from './common/BreadcrumbItem'
import ProfileService from '../services/ProfileService'
import Conditional from './common/Conditional'
import Loader from './common/Loader'
import { useMsal } from '@azure/msal-react'
import { InteractionStatus } from '@azure/msal-browser'
import { isPathMatch } from '../utilities/commonFunctions'
import axios, { CancelTokenSource } from 'axios'
import useGovCMSFilterCookie from '../hooks/useGovCMSFilterCookie'
import Register from './register/Register'
import ScrollToTop from './common/ScrollTop'
import { SocialMediaNavigationItems } from './megamenu/data/SecondaryNavigationItems'
import { NavigationHeader } from './megamenu/navigation/NavigationHeader'
import { AppLayout } from './megamenu/layouts/AppLayout'
import useLoggedInCookie from '../hooks/useLoggedInCookie'
import { INavigationItems } from '../interfaces/Navigation.Interface'
import MegamenuContentService from '../services/MegamenuContentService'
import { INavItemWithHeadingsItems } from './megamenu/interfaces/Navigation.Interface.Footer'
// eslint-disable-next-line react/display-name
const App = () => {
  const mountedRef = useRef(false)
  const { instance, inProgress } = useMsal()
  const activeAccount = instance.getActiveAccount()
  const [isLoading, setLoading] = useState<boolean>(true)
  const [setIndustryCookie, setSubIndustryCookie] = useGovCMSFilterCookie()
  const [setLoggedInCookie] = useLoggedInCookie()
  const [menu, setMenu] = useState({ showOverlay: false })

  const isRegistrationPage = window.location.pathname === PATHS.Register
  const isHomePage = window.location.pathname === PATHS.Home
  const match = isPathMatch(window.location.pathname)
  const notAllowAnonymous = match != null && !match.allowAnonymous
  const showFooterTools = match != null && match.showFooterTools
  const title = match != null && match.title
  const logInRequired = notAllowAnonymous || isHomePage || isRegistrationPage
  sessionStorage.removeItem('PageFeedback')

  const [headerMenuItems, setHeaderMenuItems] = useState<INavigationItems>()
  const [secondaryNavItems, setSecondaryNavItems] =
    useState<INavItemWithHeadingsItems>()

  const fetchHeaderMenuData = async (source: CancelTokenSource) => {
    return await MegamenuContentService.getHeaderContentLinks(source).then(
      (response) => {
        return response.result
      }
    )
  }

  useEffect(() => {
    mountedRef.current = true
    const source = axios.CancelToken.source()
    fetchHeaderMenuData(source)
      .then((header) => {
        if (mountedRef.current) {
          setHeaderMenuItems(header[0].items)
          setSecondaryNavItems(header[1].items)
        }
      })
      .finally(() => {
        if (mountedRef.current) {
          setLoading(false)
        }
      })

    return () => {
      mountedRef.current = false
      if (source) {
        source.cancel('Operation cancelled')
      }
    }
  }, [])

  const routes = Routes.map(({ component: Component, ...rest }, key) => {
    return (
      <Route
        key={key}
        {...rest}
        render={(renderProps) => {
          const filteredRoutes = Routes.filter(({ path }) =>
            renderProps.match.path.includes(path)
          ) // Swap out any dynamic routes with their param values.
            .map(({ path, ...rest }) => ({
              path: Object.keys(renderProps.match.params).length
                ? Object.keys(renderProps.match.params).reduce(
                    (path, param) =>
                      path.replace(
                        `:${param}`,
                        renderProps.match.params[param]
                      ),
                    path
                  )
                : path,
              ...rest,
            }))

          const items: IBreadcrumbItem[] = []
          filteredRoutes.map(({ parent }) => {
            parent?.map((item: IRoute) => {
              items.push({ name: item.name, path: item.path })
            })
          })
          return (
            <>
              <Conditional if={showMenu(rest)}>
                <NavMenu current={rest.path} />
              </Conditional>
              <Conditional if={rest.hideBreadcrumb === false}>
                <Breadcrumbs items={items} {...renderProps} />
              </Conditional>
              <Component {...renderProps} />
              <Conditional if={!logInRequired && !notAllowAnonymous}>
                <ScrollToTop />
              </Conditional>
            </>
          )
        }}
      />
    )
  })

  ;(function (w, d, s, l, i) {
    w[l] = w[l] || []
    w[l].push({
      'gtm.start': new Date().getTime(),
      event: 'gtm.js',
    })
    const f = d.getElementsByTagName(s)[0],
      j = d.createElement(s),
      dl = l != 'dataLayer' ? '&l=' + l : ''
    j.async = true
    j.src =
      'https://www.googletagmanager.com/gtm.js?id=' +
      i +
      dl +
      '&gtm_auth=' +
      process.env.REACT_APP_GTM_AUTH +
      '&gtm_preview=' +
      process.env.REACT_APP_GTM_PREVIEW +
      '&gtm_cookies_win=x'
    f.parentNode.insertBefore(j, f)
  })(window, document, 'script', 'dataLayer', process.env.REACT_APP_GTM_ID)

  useEffect(() => {
    mountedRef.current = true
    const source = axios.CancelToken.source()

    if (mountedRef.current && inProgress === InteractionStatus.None) {
      if (!logInRequired) {
        setLoading(false)
        return
      }

      ProfileService.getCurrentUserProfile(source)
        .then((response) => {
          const userProfile = response.result

          if (isRegistrationPage) {
            window.location.replace(PATHS.Home)
            return
          }

          if (!isRegistrationPage && userProfile) {
            setIndustryCookie(userProfile.govCMSIndustryFilterId)
            setSubIndustryCookie(userProfile.govCMSSubIndustryFilterId)
            setLoggedInCookie()
          }
        })
        .finally(() => {
          setLoading(false)
        })
    }
    return () => {
      mountedRef.current = false
      if (source) {
        source.cancel('Operation cancelled')
      }
    }
  }, [isRegistrationPage, inProgress])

  const headerClick = (value: boolean) => {
    setMenu((prevState) => ({
      ...prevState,
      showOverlay: value,
    }))
  }

  const showMenu = (rest: any) => {
    const showMenu = rest.showMenu
    return showMenu
  }

  const showMegaMenu = () => {
    if (isHomePage || isRegistrationPage) {
      return false
    }

    const match = isPathMatch(window.location.pathname)
    if (match == null) {
      return true
    }

    const showMenu = match != null && match.showMegaMenu
    return showMenu
  }

  const reload = () => window.location.reload()

  return (
    <>
      {isLoading ? (
        <Loader />
      ) : (
        <div className="fairwork-wrapper">
          <Router>
            <AppLayout>
              <Conditional if={headerMenuItems && secondaryNavItems}>
                <NavigationHeader
                  primary={headerMenuItems}
                  secondary={secondaryNavItems}
                  social={SocialMediaNavigationItems}
                  headerClick={headerClick}
                  showMegamenu={showMegaMenu()}
                />
              </Conditional>
            </AppLayout>

            <div
              id="primary-area"
              className={menu.showOverlay ? 'sr-only' : ''}
            >
              <Switch>
                <Route
                  exact
                  path="/"
                  render={() => {
                    return <Redirect to={PATHS.Home} />
                  }}
                />
                <Route exact path={PATHS.Register} component={Register} />
                <Route path="/google531835260044a3ef.html" onEnter={reload} />
                {routes}
              </Switch>
              <Footer
                showFooterTools={showFooterTools}
                showMegaFooter={showMegaMenu()}
                title={title}
                activeAccount={activeAccount}
              />
            </div>
          </Router>
        </div>
      )}
    </>
  )
}

export default App
